import type { SalesNarrativeContent, SalesNarrativeContentItem } from "@expert/contentful";

import type { Narrative, NarrativeBeat, NarrativeStep, NarrativeStepKind } from "./types";

/**
 * Assemble a `Narrative` from the CMS's named call stages. The CMS deliberately does not model steps,
 * beats or ordering, so that shape is applied here.
 *
 * Every segment is `verbatim`: each item is approved copy the expert delivers word-for-word. One
 * consequence worth knowing — `isBeatSkippable` is `!hasVerbatim`, so no beat sourced from the CMS is
 * skippable, including Guide, which was skippable while its lead-in was a hardcoded connective line.
 */
const beat = (item: SalesNarrativeContentItem, index: number, of: number): NarrativeBeat => ({
    // The item's key IS the beat id, so read/skip state survives a content swap.
    id: item.key,
    index,
    of,
    segments: [{ kind: "verbatim", text: item.text }],
});

const singleBeatStep = (kind: NarrativeStepKind, label: string, item: SalesNarrativeContentItem): NarrativeStep => ({
    kind,
    label,
    beats: [beat(item, 1, 1)],
});

export const narrativeFromContent = (content: SalesNarrativeContent): Narrative => {
    const steps: NarrativeStep[] = [singleBeatStep("transition", "Transition", content.transition)];

    // Absent for offers that are not walked through on-screen. The guided-browsing filter drops
    // whichever of guide/enroll does not apply, so both are assembled when the content has them.
    if (content.guide) steps.push(singleBeatStep("guide", "Guide", content.guide));

    // One bulleted beat listing every benefit, not a beat each — the expert reads them in one pass.
    // Id is the first item's key, so read state survives an edit that leaves that key in place.
    const [firstBenefit] = content.offerBenefits;
    if (firstBenefit) {
        steps.push({
            kind: "offer",
            label: "Offer",
            beats: [
                {
                    id: firstBenefit.key,
                    index: 1,
                    of: 1,
                    layout: "bullets",
                    segments: content.offerBenefits.map((item) => ({ kind: "verbatim" as const, text: item.text })),
                },
            ],
        });
    }
    steps.push(singleBeatStep("close", "Close", content.close));
    steps.push(singleBeatStep("enroll", "Enroll", content.enroll));

    return { steps };
};
